import React, { useState, useMemo, useEffect } from 'react';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { updateTask, updateList, getListDetails, archiveCourseAndReset, getCourseArchives, duplicateTask } from '../services/firestore';
import { BookOpen, CheckCircle2, Circle, X, Eye, Archive, Calendar, ChevronDown, RotateCcw, TrendingUp, Award, History, Power, EyeOff, BarChart3, AlertTriangle, Clock, Copy } from 'lucide-react';
import toast from 'react-hot-toast';

const DEFAULT_PHASES = [
  { key: 'fase-1', label: 'Estrategia de Marketing', short: 'F1', color: '#8b5cf6' },
  { key: 'fase-2', label: 'Comunidad & Seguimiento', short: 'F2', color: '#3b82f6' },
  { key: 'fase-3', label: 'Instructores', short: 'F3', color: '#06b6d4' },
  { key: 'fase-4', label: 'Clases en Vivo', short: 'F4', color: '#10b981' },
  { key: 'fase-5', label: 'Cierre', short: 'F5', color: '#f59e0b' },
];

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const mapSubtaskToPhase = (titulo) => {
  const t = (titulo || '').toLowerCase();
  if (t.includes('fase 1') || t.includes('marketing') || t.includes('estrategia')) return 'fase-1';
  if (t.includes('fase 2') || t.includes('comunidad') || t.includes('seguimiento')) return 'fase-2';
  if (t.includes('fase 3') || t.includes('instructor')) return 'fase-3';
  if (t.includes('fase 4') || t.includes('clases en vivo') || t.includes('clase')) return 'fase-4';
  if (t.includes('fase 5') || t.includes('cierre')) return 'fase-5';
  return null;
};

// ── Per-Course History Modal (improved) ──
const CourseHistoryModal = ({ course, archives, loading, onClose }) => {
  const [expandedArchive, setExpandedArchive] = useState(null);

  // Compute trend across months
  const trend = useMemo(() => {
    if (archives.length < 2) return null;
    const sorted = [...archives].sort((a, b) => {
      const aKey = (a.period?.year || 0) * 12 + (a.period?.month || 0);
      const bKey = (b.period?.year || 0) * 12 + (b.period?.month || 0);
      return aKey - bKey;
    });
    const first = sorted[0]?.stats?.completionRate || 0;
    const last = sorted[sorted.length - 1]?.stats?.completionRate || 0;
    return last - first;
  }, [archives]);

  // Best month
  const bestMonth = useMemo(() => {
    if (archives.length === 0) return null;
    return archives.reduce((best, a) => {
      const rate = a.stats?.completionRate || 0;
      return rate > (best.stats?.completionRate || 0) ? a : best;
    }, archives[0]);
  }, [archives]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-5 py-4 border-b border-violet-100 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                <History size={18} className="text-violet-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-800 line-clamp-1">{course.titulo}</h3>
                <p className="text-[11px] text-gray-500">{archives.length} meses archivados</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-white/60">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Summary stats bar */}
        {archives.length > 0 && (
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-3 shrink-0 overflow-x-auto">
            <div className="flex items-center gap-1.5 bg-white rounded-lg px-3 py-1.5 border border-gray-200 shrink-0">
              <BarChart3 size={12} className="text-violet-500" />
              <span className="text-[11px] text-gray-500">Promedio:</span>
              <span className="text-[12px] font-bold text-violet-600">
                {Math.round(archives.reduce((a, ar) => a + (ar.stats?.completionRate || 0), 0) / archives.length)}%
              </span>
            </div>
            {bestMonth && (
              <div className="flex items-center gap-1.5 bg-white rounded-lg px-3 py-1.5 border border-gray-200 shrink-0">
                <Award size={12} className="text-amber-500" />
                <span className="text-[11px] text-gray-500">Mejor:</span>
                <span className="text-[12px] font-bold text-amber-600">
                  {MONTH_NAMES[(bestMonth.period?.month || 1) - 1]} ({bestMonth.stats?.completionRate}%)
                </span>
              </div>
            )}
            {trend !== null && (
              <div className={`flex items-center gap-1.5 bg-white rounded-lg px-3 py-1.5 border border-gray-200 shrink-0`}>
                <TrendingUp size={12} className={trend >= 0 ? 'text-green-500' : 'text-red-500'} />
                <span className="text-[11px] text-gray-500">Tendencia:</span>
                <span className={`text-[12px] font-bold ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {trend >= 0 ? '+' : ''}{trend}%
                </span>
              </div>
            )}
          </div>
        )}

        {/* Archive list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="text-center py-10">
              <div className="w-8 h-8 border-3 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-[12px] text-gray-400">Cargando historial...</p>
            </div>
          ) : archives.length === 0 ? (
            <div className="text-center py-10">
              <Archive className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm font-medium">Sin historial</p>
              <p className="text-[11px] text-gray-400 mt-1">Archiva este curso al finalizar el mes</p>
            </div>
          ) : (
            archives.map(archive => {
              const period = archive.period || {};
              const stats = archive.stats || {};
              const rate = stats.completionRate || 0;
              const isExpanded = expandedArchive === archive.id;
              const monthName = MONTH_NAMES[(period.month || 1) - 1];
              const rateColor = rate >= 80 ? 'text-green-600 bg-green-50 border-green-200' : rate >= 50 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200';
              const subs = archive.course?.subtareas || [];
              const completedCount = subs.filter(s => s.estado === 'finalizado').length;
              const pendingCount = subs.length - completedCount;

              return (
                <div key={archive.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors">
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50/80 transition-colors"
                    onClick={() => setExpandedArchive(isExpanded ? null : archive.id)}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Calendar size={14} className="text-gray-500" />
                      </div>
                      <div>
                        <h4 className="text-[13px] font-bold text-gray-800">{monthName} {period.year}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-green-600 font-medium">{completedCount} completadas</span>
                          {pendingCount > 0 && (
                            <span className="text-[10px] text-red-500 font-medium">{pendingCount} pendientes</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full border ${rateColor}`}>
                        {rate >= 80 ? <Award size={12} /> : rate >= 50 ? <TrendingUp size={12} /> : <Circle size={12} />}
                        <span className="text-xs font-bold">{rate}%</span>
                      </div>
                      <ChevronDown size={14} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100">
                      {/* Visual progress bar */}
                      <div className="px-4 py-2 bg-gray-50/50">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${rate}%`,
                                backgroundColor: rate >= 80 ? '#10b981' : rate >= 50 ? '#f59e0b' : '#ef4444'
                              }}
                            />
                          </div>
                          <span className="text-[10px] font-bold text-gray-500">{completedCount}/{subs.length}</span>
                        </div>
                      </div>

                      {/* Task list with clear status */}
                      <div className="px-3 py-2 space-y-0.5">
                        {/* Completed tasks first */}
                        {subs.filter(s => s.estado === 'finalizado').length > 0 && (
                          <div className="mb-1.5">
                            <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wider px-1 mb-1 flex items-center gap-1">
                              <CheckCircle2 size={10} /> Completadas ({subs.filter(s => s.estado === 'finalizado').length})
                            </p>
                            {subs.filter(s => s.estado === 'finalizado').map((sub, j) => (
                              <div key={j} className="flex items-center gap-2 text-[11px] py-1 px-1 rounded hover:bg-green-50/50">
                                <CheckCircle2 size={13} className="text-green-500 shrink-0" />
                                <span className="text-gray-500 line-through flex-1">{sub.titulo}</span>
                                {sub.asignados?.length > 0 && (
                                  <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full shrink-0">
                                    {sub.asignados[0].split('@')[0]}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Pending tasks */}
                        {subs.filter(s => s.estado !== 'finalizado').length > 0 && (
                          <div>
                            <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider px-1 mb-1 flex items-center gap-1">
                              <Circle size={10} /> No completadas ({subs.filter(s => s.estado !== 'finalizado').length})
                            </p>
                            {subs.filter(s => s.estado !== 'finalizado').map((sub, j) => (
                              <div key={j} className="flex items-center gap-2 text-[11px] py-1 px-1 rounded hover:bg-red-50/50">
                                <X size={13} className="text-red-400 shrink-0" />
                                <span className="text-gray-700 flex-1">{sub.titulo}</span>
                                {sub.asignados?.length > 0 && (
                                  <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full shrink-0">
                                    {sub.asignados[0].split('@')[0]}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ── Archive Confirm Modal ──
const ArchiveConfirmModal = ({ course, period, onConfirm, onCancel, loading }) => {
  const subs = course.subtareas || [];
  const completed = subs.filter(s => s.estado === 'finalizado').length;
  const pending = subs.length - completed;
  const progress = subs.length > 0 ? Math.round(completed / subs.length * 100) : 0;
  const periodLabel = `${MONTH_NAMES[(period.month || 1) - 1]} ${period.year}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-5 border-b border-amber-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Archive size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-800">Archivar curso</h3>
              <p className="text-[12px] text-gray-500 line-clamp-1">{course.titulo} — {periodLabel}</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 space-y-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[12px] text-gray-600 font-medium mb-2">Lo que sucederá:</p>
            <ul className="space-y-1.5 text-[12px] text-gray-500">
              <li className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-green-500 mt-0.5 shrink-0" />
                Se guardará el progreso actual como snapshot
              </li>
              <li className="flex items-start gap-2">
                <RotateCcw size={14} className="text-blue-500 mt-0.5 shrink-0" />
                Todas las subtareas se reiniciarán a "Pendiente"
              </li>
            </ul>
          </div>

          {/* Current status breakdown */}
          <div className="bg-violet-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-violet-700 font-medium">Estado actual de {periodLabel}:</span>
              <span className="text-sm font-bold text-violet-700">{progress}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-violet-200 rounded-full overflow-hidden">
                <div className="h-full bg-violet-600 rounded-full" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="text-green-600 font-medium">{completed} completadas</span>
              {pending > 0 && <span className="text-red-500 font-medium">{pending} pendientes</span>}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-[13px] text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-[13px] font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Archive size={14} />
            )}
            Archivar y reiniciar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ──
const CoursesView = () => {
  const { user } = useAuth();
  const {
    filteredTasks: tasks = [],
    selectedProjectId,
    selectedFolderId,
    selectedListId,
    setSelectedTaskId,
    setShowTaskDrawer,
    setSelectedSubtaskPath,
    users = [],
  } = useTask();

  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'inactive' | 'all'
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activePeriod, setActivePeriod] = useState(null);
  const [archivingCourse, setArchivingCourse] = useState(null);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [historyCourse, setHistoryCourse] = useState(null);
  const [courseArchives, setCourseArchives] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load list period
  useEffect(() => {
    if (!user?.uid || !selectedListId) return;
    getListDetails(user.uid, selectedListId).then(data => {
      if (data?.activePeriod) {
        setActivePeriod(data.activePeriod);
      } else {
        const now = new Date();
        const defaultPeriod = { month: now.getMonth() + 1, year: now.getFullYear() };
        setActivePeriod(defaultPeriod);
        if (selectedProjectId && selectedFolderId) {
          updateList(user.uid, selectedProjectId, selectedFolderId, selectedListId, { activePeriod: defaultPeriod }).catch(() => {});
        }
      }
    }).catch(() => {});
  }, [user?.uid, selectedListId, selectedProjectId, selectedFolderId]);

  // Build courses data - ALL tasks, not just those with subtasks
  const { courses, phaseMap } = useMemo(() => {
    const courseList = [];
    const phases = {};
    tasks.forEach(task => {
      courseList.push(task);
      const subtasks = task.subtareas || [];
      phases[task.id] = {};
      subtasks.forEach(sub => {
        const phase = mapSubtaskToPhase(sub.titulo);
        const key = phase || 'other';
        if (!phases[task.id][key]) phases[task.id][key] = [];
        phases[task.id][key].push(sub);
      });
    });
    return { courses: courseList, phaseMap: phases };
  }, [tasks]);

  // Count active/inactive
  const activeCourseCount = courses.filter(c => c.cursoActivo !== false).length;
  const inactiveCourseCount = courses.filter(c => c.cursoActivo === false).length;

  // Filter (search already handled by TopBar → filteredTasks)
  const filteredCourses = useMemo(() => {
    let result = courses;
    if (statusFilter === 'active') result = result.filter(c => c.cursoActivo !== false);
    else if (statusFilter === 'inactive') result = result.filter(c => c.cursoActivo === false);
    if (phaseFilter !== 'all') {
      result = result.filter(c => {
        const ph = phaseMap[c.id] || {};
        return (ph[phaseFilter] || []).some(t => t.estado !== 'finalizado');
      });
    }
    return result;
  }, [courses, statusFilter, phaseFilter, phaseMap]);

  // Helpers
  const getProgress = (course) => {
    const subs = course.subtareas || [];
    if (subs.length === 0) return 0;
    return Math.round(subs.filter(s => s.estado === 'finalizado').length / subs.length * 100);
  };

  const getCurrentPhase = (course) => {
    const phases = phaseMap[course.id] || {};
    for (const phase of DEFAULT_PHASES) {
      const pt = phases[phase.key] || [];
      if (pt.length > 0 && !pt.every(t => t.estado === 'finalizado')) return phase;
    }
    return DEFAULT_PHASES[DEFAULT_PHASES.length - 1];
  };

  const getPhaseProgress = (phaseTasks) => {
    if (!phaseTasks || phaseTasks.length === 0) return 0;
    return Math.round(phaseTasks.filter(t => t.estado === 'finalizado').length / phaseTasks.length * 100);
  };

  const handleTaskClick = (taskId, subtaskId = null) => {
    setSelectedTaskId(taskId);
    setSelectedSubtaskPath(subtaskId ? [subtaskId] : []);
    setShowTaskDrawer(true);
  };

  const handleToggleSubtask = async (course, subtaskId, currentStatus) => {
    const newStatus = currentStatus === 'finalizado' ? 'en-curso' : 'finalizado';
    const updatedSubs = (course.subtareas || []).map(s =>
      s.id === subtaskId ? { ...s, estado: newStatus } : s
    );
    try {
      await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, course.id, { subtareas: updatedSubs });
      if (newStatus === 'finalizado') toast.success('Tarea completada');
    } catch { toast.error('Error al actualizar'); }
  };

  // Toggle course active/inactive
  const handleToggleCourseActive = async (course) => {
    const newValue = course.cursoActivo === false ? true : false;
    try {
      await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, course.id, { cursoActivo: newValue });
      toast.success(newValue ? 'Curso activado' : 'Curso desactivado');
    } catch { toast.error('Error al cambiar estado'); }
  };

  const getAssigneeName = (email) => {
    const u = users.find(usr => usr.email === email);
    return u?.displayName?.charAt(0)?.toUpperCase() || (email || '?').charAt(0).toUpperCase();
  };

  const getAssigneePhoto = (email) => {
    return users.find(u => u.email === email)?.photoURL || null;
  };

  // Parse Firebase timestamp or date string
  const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === 'object' && d.seconds) return new Date(d.seconds * 1000);
    return new Date(d);
  };

  // Overdue detection
  const getOverdueInfo = (course) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const overdueSubs = [];
    // Check main task
    if (course.fechaVencimiento && course.estado !== 'finalizado') {
      const dueDate = parseDate(course.fechaVencimiento);
      if (dueDate && dueDate < today) {
        overdueSubs.push({ titulo: course.titulo, fechaVencimiento: course.fechaVencimiento, isMain: true });
      }
    }
    // Check subtasks
    (course.subtareas || []).forEach(sub => {
      if (sub.fechaVencimiento && sub.estado !== 'finalizado') {
        const dueDate = parseDate(sub.fechaVencimiento);
        if (dueDate && dueDate < today) {
          overdueSubs.push({ titulo: sub.titulo, fechaVencimiento: sub.fechaVencimiento });
        }
      }
    });
    return overdueSubs;
  };

  // Per-course archive
  const handleArchiveCourse = async () => {
    if (!user?.uid || !activePeriod || !selectedProjectId || !selectedFolderId || !selectedListId || !archivingCourse) return;
    setLoadingArchive(true);
    try {
      const result = await archiveCourseAndReset(user.uid, selectedProjectId, selectedFolderId, selectedListId, archivingCourse, activePeriod);
      const monthName = MONTH_NAMES[(activePeriod.month || 1) - 1];
      toast.success(`"${archivingCourse.titulo}" archivado (${monthName} — ${result.progress}%)`);
      setArchivingCourse(null);
    } catch (err) {
      console.error(err);
      toast.error('Error al archivar el curso');
    } finally {
      setLoadingArchive(false);
    }
  };

  // Per-course history
  const handleShowHistory = async (course) => {
    if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) return;
    setHistoryCourse(course);
    setLoadingHistory(true);
    try {
      const archives = await getCourseArchives(user.uid, selectedProjectId, selectedFolderId, selectedListId, course.id);
      setCourseArchives(archives);
    } catch {
      setCourseArchives([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Stats (only active courses)
  const activeCoursesData = courses.filter(c => c.cursoActivo !== false);
  const avgProgress = activeCoursesData.length > 0 ? Math.round(activeCoursesData.reduce((a, c) => a + getProgress(c), 0) / activeCoursesData.length) : 0;
  const totalSubtasks = activeCoursesData.reduce((a, c) => a + (c.subtareas || []).length, 0);
  const completedSubtasks = activeCoursesData.reduce((a, c) => a + (c.subtareas || []).filter(s => s.estado === 'finalizado').length, 0);

  const periodLabel = activePeriod ? `${MONTH_NAMES[(activePeriod.month || 1) - 1]} ${activePeriod.year}` : '';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Thin inline filter strip - no duplicate header */}
      <div className="px-3 py-1.5 bg-white border-b border-gray-100 flex items-center gap-1.5 overflow-x-auto scrollbar-hide shrink-0">
        {/* Period badge */}
        {activePeriod && (
          <span className="flex items-center gap-1 bg-violet-50 text-violet-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-violet-200 shrink-0">
            <Calendar size={10} /> {periodLabel}
          </span>
        )}

        <div className="w-px h-4 bg-gray-200 shrink-0" />

        {/* Active/Inactive/All */}
        {[
          { key: 'active', label: 'Activos', count: activeCourseCount, activeClass: 'bg-green-50 border-green-200 text-green-700' },
          { key: 'inactive', label: 'Inactivos', count: inactiveCourseCount, activeClass: 'bg-gray-200 border-gray-300 text-gray-700' },
          { key: 'all', label: 'Todos', count: courses.length, activeClass: 'bg-violet-50 border-violet-200 text-violet-700' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 transition-all border ${
              statusFilter === f.key ? f.activeClass : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {f.label}
            <span className="text-[9px] opacity-70">{f.count}</span>
          </button>
        ))}

        <div className="w-px h-4 bg-gray-200 shrink-0" />

        {/* Stats */}
        <span className="text-[10px] font-bold text-violet-600 shrink-0">{avgProgress}%</span>
        <span className="text-[10px] text-green-600 font-medium shrink-0">{completedSubtasks}/{totalSubtasks}</span>

        <div className="w-px h-4 bg-gray-200 shrink-0" />

        {/* Phase filters */}
        {DEFAULT_PHASES.map(p => (
          <button
            key={p.key}
            onClick={() => setPhaseFilter(phaseFilter === p.key ? 'all' : p.key)}
            className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium shrink-0 transition-all border ${phaseFilter === p.key
              ? 'text-white border-transparent'
              : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
            style={phaseFilter === p.key ? { backgroundColor: p.color } : {}}
          >
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: phaseFilter === p.key ? 'white' : p.color }} />
            {p.short}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {filteredCourses.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm font-medium">No hay cursos</p>
          </div>
        ) : (
          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredCourses.map(course => {
                const isInactive = course.cursoActivo === false;
                const progress = getProgress(course);
                const currentPhase = getCurrentPhase(course);
                const phases = phaseMap[course.id] || {};
                const subs = course.subtareas || [];
                const completedSubs = subs.filter(s => s.estado === 'finalizado').length;
                const isComplete = progress === 100;
                const isSelected = selectedCourse === course.id;
                const overdueItems = getOverdueInfo(course);

                return (
                  <div
                    key={course.id}
                    className={`bg-white rounded-xl border transition-all duration-200 cursor-pointer group hover:shadow-md relative
                      ${isSelected ? 'border-violet-400 shadow-lg ring-2 ring-violet-100' : 'border-gray-200 hover:border-gray-300'}
                      ${isInactive ? 'opacity-50 grayscale' : ''}
                      ${overdueItems.length > 0 && !isInactive ? 'border-red-200 hover:border-red-300' : ''}
                      ${isComplete && !isInactive ? 'opacity-60' : ''}`}
                    onClick={() => setSelectedCourse(isSelected ? null : course.id)}
                  >
                    {/* Overdue alert badge */}
                    {overdueItems.length > 0 && !isInactive && (
                      <div className="absolute -top-1.5 -right-1.5 z-10 bg-red-500 text-white text-[9px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-sm animate-pulse" title={`${overdueItems.length} tarea(s) vencida(s)`}>
                        {overdueItems.length}
                      </div>
                    )}

                    {/* Inactive overlay badge */}
                    {isInactive && (
                      <div className="absolute top-2 right-2 z-10 bg-gray-800 text-white text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <EyeOff size={10} /> Desactivado
                      </div>
                    )}

                    {/* Card Top */}
                    <div className="p-3 pb-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white" style={{ backgroundColor: currentPhase.color }}>
                          {currentPhase.short}
                        </span>
                        <span className={`text-sm font-bold ${isComplete ? 'text-green-600' : 'text-gray-700'}`}>{progress}%</span>
                      </div>

                      <h3
                        className={`text-[13px] font-semibold leading-tight mb-1.5 line-clamp-2 group-hover:text-violet-700 transition-colors ${isComplete ? 'text-gray-400 line-through' : 'text-gray-800'}`}
                        onClick={(e) => { e.stopPropagation(); handleTaskClick(course.id); }}
                      >
                        {course.titulo}
                      </h3>

                      <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        <span>{completedSubs}/{subs.length} tareas</span>
                      </div>
                    </div>

                    {/* Phase bars */}
                    <div className="px-3 pb-2">
                      <div className="flex gap-[3px] h-[6px] rounded-full overflow-hidden">
                        {DEFAULT_PHASES.map(phase => {
                          const pt = phases[phase.key] || [];
                          const pp = getPhaseProgress(pt);
                          return (
                            <div key={phase.key} className="flex-1 rounded-sm relative" style={{ backgroundColor: `${phase.color}20` }} title={`${phase.label}: ${pp}%`}>
                              <div className="absolute inset-y-0 left-0 rounded-sm transition-all duration-500" style={{ width: `${pp}%`, backgroundColor: phase.color }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Assignees + per-course actions */}
                    <div className="px-3 pb-2 flex items-center justify-between">
                      <div className="flex -space-x-1.5">
                        {[...new Set(subs.flatMap(s => s.asignados || []))].slice(0, 5).map((email, i) => {
                          const photo = getAssigneePhoto(email);
                          return (
                            <div key={i} className="w-5 h-5 rounded-full border-2 border-white overflow-hidden shrink-0" title={email}>
                              {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : (
                                <div className="w-full h-full bg-gradient-to-br from-violet-400 to-purple-500 text-white text-[8px] font-bold flex items-center justify-center">
                                  {getAssigneeName(email)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Hover action buttons */}
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleCourseActive(course); }}
                          className={`p-1.5 rounded-lg transition-colors ${isInactive ? 'text-green-500 hover:bg-green-50' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                          title={isInactive ? 'Activar curso' : 'Desactivar curso'}
                        >
                          <Power size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleShowHistory(course); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                          title="Ver historial"
                        >
                          <History size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setArchivingCourse(course); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          title={`Archivar ${periodLabel}`}
                        >
                          <Archive size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Expanded task list */}
                    {isSelected && (
                      <div className="border-t border-gray-100 max-h-[300px] overflow-y-auto">
                        {/* Overdue alert banner */}
                        {overdueItems.length > 0 && (
                          <div className="px-3 py-1.5 bg-red-50 border-b border-red-100 flex items-center gap-1.5">
                            <AlertTriangle size={12} className="text-red-500 shrink-0" />
                            <span className="text-[11px] text-red-600 font-medium">
                              {overdueItems.length} tarea{overdueItems.length > 1 ? 's' : ''} vencida{overdueItems.length > 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                        {DEFAULT_PHASES.map(phase => {
                          const pt = phases[phase.key] || [];
                          if (pt.length === 0) return null;
                          const pp = getPhaseProgress(pt);
                          return (
                            <div key={phase.key}>
                              <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50/80 sticky top-0">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: phase.color }} />
                                  <span className="text-[11px] font-semibold text-gray-600">{phase.short}: {phase.label}</span>
                                </div>
                                <span className="text-[10px] font-medium" style={{ color: phase.color }}>{pp}%</span>
                              </div>
                              {pt.map(sub => (
                                <div key={sub.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50/50 transition-colors">
                                  <button onClick={(e) => { e.stopPropagation(); handleToggleSubtask(course, sub.id, sub.estado); }} className="shrink-0">
                                    {sub.estado === 'finalizado'
                                      ? <CheckCircle2 size={14} className="text-green-500" />
                                      : <Circle size={14} className="text-gray-300 hover:text-violet-400 transition-colors" />}
                                  </button>
                                  <span
                                    className={`text-[12px] flex-1 truncate cursor-pointer hover:text-violet-600 ${sub.estado === 'finalizado' ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                                    onClick={(e) => { e.stopPropagation(); handleTaskClick(course.id, sub.id); }}
                                  >
                                    {sub.titulo}
                                  </span>
                                  {sub.fechaVencimiento && sub.estado !== 'finalizado' && new Date(sub.fechaVencimiento) < new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()) && (
                                    <span className="text-[9px] text-red-500 font-medium flex items-center gap-0.5 shrink-0" title="Vencida">
                                      <Clock size={10} />
                                    </span>
                                  )}
                                  {sub.asignados?.slice(0, 1).map((email, i) => {
                                    const photo = getAssigneePhoto(email);
                                    return photo
                                      ? <img key={i} src={photo} alt="" className="w-4 h-4 rounded-full shrink-0" />
                                      : <div key={i} className="w-4 h-4 rounded-full bg-violet-100 text-violet-600 text-[8px] font-bold flex items-center justify-center shrink-0">{getAssigneeName(email)}</div>;
                                  })}
                                </div>
                              ))}
                            </div>
                          );
                        })}

                        {phases['other']?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50/80 sticky top-0">
                              <div className="w-2 h-2 rounded-full bg-gray-400" />
                              <span className="text-[11px] font-semibold text-gray-500">Otras tareas</span>
                            </div>
                            {phases['other'].map(sub => (
                              <div key={sub.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50">
                                <button onClick={(e) => { e.stopPropagation(); handleToggleSubtask(course, sub.id, sub.estado); }} className="shrink-0">
                                  {sub.estado === 'finalizado' ? <CheckCircle2 size={14} className="text-green-500" /> : <Circle size={14} className="text-gray-300" />}
                                </button>
                                <span className={`text-[12px] flex-1 truncate ${sub.estado === 'finalizado' ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{sub.titulo}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Card footer */}
                        <div className="px-3 py-2 border-t border-gray-100 flex items-center gap-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleTaskClick(course.id); }}
                            className="flex-1 flex items-center justify-center gap-1.5 text-[11px] text-violet-600 hover:text-violet-700 font-medium py-1.5 rounded-lg hover:bg-violet-50 transition-colors"
                          >
                            <Eye size={12} /> Detalle
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleCourseActive(course); }}
                            className={`flex items-center gap-1 text-[11px] font-medium py-1.5 px-2 rounded-lg transition-colors ${
                              isInactive
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <Power size={12} /> {isInactive ? 'Activar' : 'Desactivar'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleShowHistory(course); }}
                            className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-violet-600 font-medium py-1.5 px-2 rounded-lg hover:bg-violet-50 transition-colors"
                          >
                            <History size={12} /> Historial
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setArchivingCourse(course); }}
                            className="flex items-center gap-1 text-[11px] text-amber-600 hover:text-amber-700 font-medium py-1.5 px-2 rounded-lg hover:bg-amber-50 transition-colors"
                          >
                            <Archive size={12} /> Archivar
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await duplicateTask(user.uid, course.projectId, course.folderId, course.listId, course);
                                toast.success(`Curso duplicado con ${course.subtareas?.length || 0} subtareas`);
                              } catch { toast.error('Error al duplicar'); }
                            }}
                            className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 font-medium py-1.5 px-2 rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            <Copy size={12} /> Duplicar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Archive Confirmation Modal */}
      {archivingCourse && activePeriod && (
        <ArchiveConfirmModal
          course={archivingCourse}
          period={activePeriod}
          onConfirm={handleArchiveCourse}
          onCancel={() => setArchivingCourse(null)}
          loading={loadingArchive}
        />
      )}

      {/* History Modal */}
      {historyCourse && (
        <CourseHistoryModal
          course={historyCourse}
          archives={courseArchives}
          loading={loadingHistory}
          onClose={() => { setHistoryCourse(null); setCourseArchives([]); }}
        />
      )}
    </div>
  );
};

export default CoursesView;
